import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
} from "@chakra-ui/react";
import { FiSearch } from "react-icons/fi";
import { useSession } from "./session";
import ForYou, { ForYouSkeleton } from "./ForYou";
import CharacterWall from "./CharacterWall";

const FOR_YOU_POOL = 30;

function shuffled(list) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Fetches pages on demand; remounted (via key) whenever the query or sort changes. */
function Feed({ q, sort, showForYou }) {
  const { api, auth } = useSession();
  const [items, setItems] = useState([]),
    [pool, setPool] = useState([]),
    [page, setPage] = useState(1),
    [hasMore, setHasMore] = useState(false),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [retry, setRetry] = useState(0);
  const sentinel = useRef(null),
    itemsRef = useRef([]);

  useEffect(() => {
    if (auth.isLoading) return;
    const abort = new AbortController();
    setLoading(true);
    setError("");
    const query = new URLSearchParams({ q, sort, page: String(page) });
    api(`/api/search?${query}`, { signal: abort.signal })
      .then((r) => r.json())
      .then((d) => {
        if (abort.signal.aborted) return;
        const seen = new Set(itemsRef.current.map((c) => c.publicId));
        const fresh = d.results.filter((c) => !seen.has(c.publicId));
        itemsRef.current = [...itemsRef.current, ...fresh];
        setItems(itemsRef.current);
        if (page === 1) setPool(shuffled(d.results.slice(0, FOR_YOU_POOL)));
        // A page with nothing new means the feed is exhausted; stop asking for more.
        setHasMore(d.hasMore && fresh.length > 0);
      })
      .catch((e) => {
        if (e.name !== "AbortError") setError(e.message);
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  }, [api, auth.isLoading, q, sort, page, retry]);

  // Re-create the observer after every load so a sentinel that is still visible triggers the next page.
  useEffect(() => {
    const el = sentinel.current;
    if (!el || loading || error || !hasMore) return;
    if (typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setPage((p) => p + 1);
      },
      { rootMargin: "0px 0px 480px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loading, error, hasMore, items.length]);

  const firstLoad = loading && !items.length;
  return (
    <>
      {showForYou &&
        (firstLoad ? (
          <ForYouSkeleton />
        ) : (
          pool.length > 0 && <ForYou pool={pool} />
        ))}
      {error && !items.length ? (
        <div className="empty-state" role="alert">
          <h2>Could not load characters</h2>
          <p>{error}</p>
          <Button onClick={() => setRetry((v) => v + 1)}>Try again</Button>
        </div>
      ) : !loading && !items.length ? (
        <div className="empty-state">
          <FiSearch />
          <h2>No characters found</h2>
          <p>Try a different search.</p>
        </div>
      ) : (
        <>
          <CharacterWall items={items} loading={firstLoad} />
          <div className="wall-more" ref={sentinel}>
            {loading && items.length > 0 && <Spinner size="sm" />}
            {error && items.length > 0 && (
              <Button size="sm" onClick={() => setRetry((v) => v + 1)}>
                Try again
              </Button>
            )}
            {!loading && !error && !hasMore && items.length > 0 && (
              <span>No more characters</span>
            )}
          </div>
        </>
      )}
    </>
  );
}

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "",
    sort = params.get("sort") || (q ? "relevance" : "popular");
  const [keyword, setKeyword] = useState(q);
  useEffect(() => setKeyword(q), [q]);

  const update = (values) => {
    const next = new URLSearchParams(params);
    Object.entries(values).forEach(([k, v]) => {
      v ? next.set(k, String(v)) : next.delete(k);
    });
    setParams(next);
  };
  const submit = (e) => {
    e.preventDefault();
    update({ q: keyword.trim(), sort: "" });
  };
  const tabs = [
    ...(q ? [["relevance", "Relevant"]] : []),
    ["popular", q ? "Popular" : "Recommend"],
    ["newest", "Newest"],
    ["likes", "Most liked"],
    ["visits", "Most visited"],
  ];

  return (
    <div className="discover-page search-page">
      <h1 className="sr-only">Search characters</h1>
      <form className="search-bar" onSubmit={submit}>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <FiSearch />
          </InputLeftElement>
          <Input
            aria-label="Search characters or stories"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Search characters or stories"
          />
          <Button className="search-submit" type="submit" variant="brand">
            Search
          </Button>
        </InputGroup>
      </form>
      {q && <p className="search-summary">Results for “{q}”</p>}
      <div className="browse-toolbar">
        <div className="sort-tabs" role="group" aria-label="Sort characters">
          {tabs.map(([key, label]) => (
            <button
              key={key}
              className={sort === key ? "selected" : ""}
              aria-pressed={sort === key}
              onClick={() =>
                update({
                  sort: key === (q ? "relevance" : "popular") ? "" : key,
                })
              }
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <Feed
        key={`${q}|${sort}`}
        q={q}
        sort={sort}
        showForYou={!q && sort === "popular"}
      />
    </div>
  );
}
